import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePlanningPeriod } from "@/context/PlanningPeriodContext";
import { PlanningPeriodSelector } from "@/components/PlanningPeriodSelector";
import { ServiceCalendar, ServiceDetails, AssignedSlots } from "@/components/ServiceCalendar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users, CalendarCheck, CalendarX, Scale, Repeat, TrendingUp, TrendingDown,
  Loader2, Rocket, CircleCheck, CircleAlert, CircleX, Send, ScanLine, Clock,
} from "lucide-react";

const CLR = {
  red: { bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-600" },
  sky: { bg: "bg-sky-100 dark:bg-sky-900/40", text: "text-sky-600" },
  amber: { bg: "bg-amber-100 dark:bg-amber-900/40", text: "text-amber-600" },
  rose: { bg: "bg-rose-100 dark:bg-rose-900/40", text: "text-rose-600" },
};

function Kpi({ icon: Icon, label, value, sub, color = "red" }) {
  const c = CLR[color];
  return (
    <Card className="p-5" data-testid={`kpi-${label}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-3xl font-bold font-heading">{value}</p>
          <p className="text-sm text-muted-foreground mt-1">{label}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${c.text}`} />
        </div>
      </div>
    </Card>
  );
}

const CHECK_ICON = { ok: CircleCheck, warning: CircleAlert, error: CircleX };
const CHECK_COLOR = { ok: "text-red-600", warning: "text-amber-600", error: "text-rose-600" };

export default function PlannerDashboard() {
  const { period, setPeriod, query, label } = usePlanningPeriod();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [services, setServices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [todayStatus, setTodayStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pubOpen, setPubOpen] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dashboard, serviceList, assignList] = await Promise.all([
        api.get(`/planning/dashboard?${query}`),
        api.get(`/services?${query}`),
        api.get(`/planning/assignments?${query}`),
      ]);
      setData(dashboard.data);
      setServices(serviceList.data.filter((s) => s.date >= period.date_from && s.date <= period.date_to));
      setAssignments(assignList.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { setPubOpen(false); load(); }, [query]);

  useEffect(() => {
    let active = true;
    api.get("/attendance/today-status")
      .then((r) => { if (active) setTodayStatus(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const openPublish = async () => {
    setPubOpen(true);
    setChecklist(null);
    try {
      const { data } = await api.get(`/planning/publish-check?${query}`);
      setChecklist(data);
    } catch (e) {
      setPubOpen(false);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doPublish = async (mode) => {
    setPublishing(true);
    try {
      const { data } = await api.post(`/planning/publish?${query}&mode=${mode}`);
      const base = mode === "final" ? "Definitief gepubliceerd" : "Voorlopig gepubliceerd";
      let msg = `${base} — ${data.published_count} dienst(en), ${data.notified} vrijwilliger(s) genotificeerd`;
      if (mode === "final" && data.skipped_dates?.length) msg += ` · ${data.skipped_dates.length} datum(s) al definitief (overgeslagen)`;
      toast.success(msg);
      setPubOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="planner-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Planner dashboard</h1>
          <p className="text-muted-foreground mt-1" data-testid="planning-publication-status">Doorlopende diensten · {data?.final_count || 0} definitief · {data?.provisional_count || 0} voorlopig · {data?.concept_count || 0} concept in deze periode</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate("/board")} data-testid="goto-board"><Scale className="w-4 h-4 mr-1.5" /> Naar planbord</Button>
          <Button onClick={openPublish} disabled={loading || !data?.services_count} data-testid="open-publish"><Rocket className="w-4 h-4 mr-1.5" /> Periode publiceren</Button>
        </div>
      </div>
      <PlanningPeriodSelector />
      {todayStatus?.enabled && todayStatus.assigned_count > 0 && (
        <Card className="p-6" data-testid="today-attendance">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h3 className="font-heading font-semibold flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-red-600" /> Vandaag ingepland · aanwezigheid
            </h3>
            <span className="text-sm font-medium text-muted-foreground" data-testid="today-attendance-count">
              {todayStatus.checked_in_count}/{todayStatus.assigned_count} ingecheckt
            </span>
          </div>
          {todayStatus.not_checked_in.length === 0 ? (
            <p className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2" data-testid="today-attendance-all-in">
              <CircleCheck className="w-4 h-4" /> Iedereen die vandaag is ingepland, heeft ingecheckt.
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Nog niet ingecheckt:</p>
              {todayStatus.not_checked_in.map((u) => (
                <div key={u.user_id} data-testid={`not-checked-in-${u.user_id}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20 px-3 py-2.5">
                  <span className="font-medium">{u.user_name}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> {u.service_name} · {u.service_time}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      {!loading && data?.services_count === 0 && (
        <Card className="p-6 space-y-3" data-testid="planner-empty-state">
          <h2 className="font-heading text-lg font-semibold">Plan je eerste dienst</h2>
          <p className="text-sm text-muted-foreground" data-testid="planner-empty-description">Geen diensten in deze periode. Voeg rechtstreeks diensten toe, los of in bulk. Werkzaamheden stel je als beheerder in via Instellingen.</p>
          <Button onClick={() => navigate("/services")} data-testid="planner-create-first-service">Dienst aanmaken</Button>
        </Card>
      )}
      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={CalendarCheck} label="Diensten" value={data.services_count} sub={`${data.total_slots} plekken`} />
            <Kpi icon={Users} label="Bezet" value={data.filled} sub={`${data.open} open`} color="sky" />
            <Kpi icon={Scale} label="Gemiddelde" value={data.avg_shifts} sub="diensten/vrijwilliger" color="amber" />
            <Kpi icon={Repeat} label="Open ruilverzoeken" value={data.open_swaps} color="sky" />
          </div>

          <ServiceCalendar services={services} calendarId="planner-dashboard" periodStart={period.date_from} onPeriodChange={setPeriod}
            getStatus={(s) => {
              const have = data.service_occupancy?.[s.id] || 0;
              const need = s.slots.reduce((n, sl) => n + sl.desired, 0);
              return { tone: have >= need ? "red" : "amber", label: `${have}/${need} ingepland` };
            }}
            getNames={(s) => assignments.filter((a) => a.service_id === s.id).map((a) => a.user_name)}
            renderDetails={(s) => <ServiceDetails service={s}><AssignedSlots slots={s.slots} assignments={assignments.filter((a) => a.service_id === s.id)} /><Button onClick={() => navigate(`/board?service=${s.id}`)} variant="outline" size="sm" data-testid={`dashboard-plan-${s.id}`}>Medewerkers inplannen</Button></ServiceDetails>} />
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-heading font-semibold mb-4">Bezetting diensten</h3>
              <div className="space-y-3">
                <Row icon={CircleCheck} color="red" label="Volledig bezet" value={data.services_full} />
                <Row icon={CircleAlert} color="amber" label="Bijna volledig" value={data.services_partial} />
                <Row icon={CircleX} color="rose" label="Onbezet" value={data.services_empty} />
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="font-heading font-semibold mb-4">Verdeling</h3>
              <div className="space-y-3">
                <Row icon={TrendingUp} color="amber" label="Boven de norm" value={data.over_norm} />
                <Row icon={TrendingDown} color="sky" label="Onder de norm" value={data.under_norm} />
                <Row icon={Users} color="red" label="Ingeplande vrijwilligers" value={data.volunteers_planned} />
              </div>
              <Button variant="link" className="px-0 mt-2" onClick={() => navigate("/distribution")}>Bekijk volledige verdeling →</Button>
            </Card>
          </div>
        </>
      )}

      <Dialog open={pubOpen} onOpenChange={setPubOpen}>
        <DialogContent data-testid="publish-dialog">
          <DialogHeader><DialogTitle>Planning publiceren — controle</DialogTitle></DialogHeader>
          <p className="text-sm" data-testid="publish-period">{label}. <strong>Voorlopig</strong> publiceren kun je zo vaak je wilt; <strong>definitief</strong> stelt de planning per datum eenmalig vast. Alleen diensten met een begindatum in deze periode worden aangepast.</p>
          {checklist && (
            <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1" data-testid="publish-state-summary">
              <span>Concept: <strong>{checklist.concept_count}</strong></span>
              <span>Voorlopig: <strong>{checklist.provisional_count}</strong></span>
              <span>Definitief: <strong>{checklist.final_count}</strong></span>
            </div>
          )}
          {checklist?.finalized_dates?.length > 0 && (
            <p className="text-xs text-amber-700" data-testid="publish-finalized-note">
              {checklist.finalized_dates.length} datum(s) in deze periode zijn al definitief vastgesteld en worden niet opnieuw definitief gepubliceerd.
            </p>
          )}
          {!checklist ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-2">
              {checklist.checklist.map((c, i) => {
                const Ic = CHECK_ICON[c.level];
                return (
                  <div key={i} className="flex items-center gap-2 text-sm" data-testid={`check-${i}`}>
                    <Ic className={`w-5 h-5 shrink-0 ${CHECK_COLOR[c.level]}`} />
                    <span>{c.label}</span>
                  </div>
                );
              })}
              {!checklist.can_publish && (
                <p className="text-sm text-rose-600 mt-2 font-medium">Kritieke fouten moeten eerst opgelost worden.</p>
              )}
              {checklist.can_publish && checklist.checklist.some((c) => c.level === "warning") && (
                <p className="text-sm text-amber-600 mt-2">Er zijn waarschuwingen. Publiceren mag; ze worden geregistreerd.</p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setPubOpen(false)} data-testid="cancel-publish">Annuleren</Button>
            <Button variant="outline" onClick={() => doPublish("provisional")} disabled={!checklist?.can_publish || publishing} data-testid="confirm-publish-provisional">
              {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Voorlopig publiceren
            </Button>
            <Button onClick={() => doPublish("final")} disabled={!checklist?.can_publish || publishing} data-testid="confirm-publish-final">
              {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
              Definitief publiceren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ icon: Icon, color, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm"><Icon className={`w-4 h-4 ${CLR[color].text}`} /> {label}</span>
      <span className="font-bold font-heading">{value}</span>
    </div>
  );
}
